import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const AVATARS = [
  { id: 'avatar1', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=FFD700', label: 'Felix' },
  { id: 'avatar2', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka&backgroundColor=FFD700', label: 'Aneka' },
  { id: 'avatar3', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Riley&backgroundColor=FFD700', label: 'Riley' },
  { id: 'avatar4', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jordan&backgroundColor=FFD700', label: 'Jordan' },
  { id: 'avatar5', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex&backgroundColor=FFD700', label: 'Alex' },
  { id: 'avatar6', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Taylor&backgroundColor=FFD700', label: 'Taylor' },
  { id: 'avatar7', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Morgan&backgroundColor=FFD700', label: 'Morgan' },
  { id: 'avatar8', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Casey&backgroundColor=FFD700', label: 'Casey' },
];

const Auth = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    avatar: null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back');
      } else {
        if (!formData.name.trim()) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.password, formData.avatar);
        toast.success('Account created');
      }
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="p-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-black/60 hover:text-black text-sm body-text"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </button>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10 animate-fade-in">
            <h1 className="text-3xl headline text-[#1E491A] mb-2" style={{ fontFamily: "var(--font)", letterSpacing: '-1.5px', fontWeight: 700, textTransform: 'lowercase' }}>spring</h1>
            <p className="text-black/60 body-text">
              {isLogin ? 'Welcome back' : 'Create your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 animate-slide-up">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm subhead text-black">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-secondary border-2 border-transparent focus:border-primary rounded-none h-12 text-base body-text input-clean"
                    data-testid="name-input"
                  />
                </div>

                {/* Avatar Picker */}
                <div className="space-y-2">
                  <Label className="text-sm subhead text-black">Choose your avatar</Label>
                  <div className="grid grid-cols-4 gap-3">
                    {AVATARS.map((av) => (
                      <button
                        key={av.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, avatar: av.url })}
                        data-testid={`avatar-${av.id}`}
                        className="relative"
                        style={{
                          width: '64px', height: '64px', borderRadius: 0,
                          border: formData.avatar === av.url ? '3px solid #F6ADBC' : '3px solid #f0f0f0',
                          padding: '2px', cursor: 'pointer', overflow: 'hidden',
                          transition: 'border-color 0.15s, transform 0.15s',
                          transform: formData.avatar === av.url ? 'scale(1.1)' : 'scale(1)',
                          background: '#f8f8f8',
                        }}
                      >
                        <img src={av.url} alt={av.label} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                        {formData.avatar === av.url && (
                          <div style={{
                            position: 'absolute', bottom: '-2px', right: '-2px',
                            width: '20px', height: '20px', borderRadius: '50%',
                            background: '#F6ADBC', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Check style={{ width: 12, height: 12, color: '#1E491A' }} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm subhead text-black">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-secondary border-2 border-transparent focus:border-primary rounded-none h-12 text-base body-text input-clean"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm subhead text-black">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="bg-secondary border-2 border-transparent focus:border-primary rounded-none h-12 text-base body-text input-clean"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-none h-12 text-base subhead btn-spring"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating...'}
                </>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setFormData({ name: '', email: '', password: '', avatar: null });
              }}
              className="text-sm text-black/60 hover:text-black body-text underline underline-offset-4"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Auth;
